import { fetchAivaProcess } from '../../data/questionnaires/actions';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import Dll from '@visma/formula/lib/dll';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryLabel,
} from 'victory';
import OTButton from '../tag/OTButton';
import { useHistory } from 'react-router-dom';

const PATIENT_CHART_COLOR = 'deepskyblue';
const PRO_CHART_COLOR = 'seagreen';
const RELATIVE_CHART_COLOR = 'orangered';

const mapToChartData = (data, aivaFields) => {
  const aivaData = [];
  const formDataKeys = Object.keys(data);

  aivaFields.forEach((aivaField) => {
    const fieldFullId = formDataKeys.find((key) =>
      key.startsWith(`f_${aivaField.fieldId ?? aivaField.id}_`)
    );
    if (fieldFullId) {
      aivaData.push({ x: `${aivaField.id}`, y: data[fieldFullId] });
    }
  });

  return aivaData;
};

const getOtherInfo = (data, fields) => {
  const otherInfo = {};
  const dataKeys = Object.keys(data);

  fields.forEach((field) => {
    const fieldFullId = dataKeys.find((key) =>
      key.startsWith(`f_${field.id}_`)
    );
    if (fieldFullId) {
      otherInfo[field.meta.aivaIdentifier] = data[fieldFullId];
    }
  });

  return otherInfo;
};

const Chart = ({ aivaFields, patientFormData, otherFormData, colorScale }) => {
  return (
    <VictoryChart
      horizontal
      padding={{ right: 170, bottom: 50, left: 10 }}
      domain={{ y: [-4, 4] }}
      width={300}
      height={patientFormData.length * 20}
    >
      <VictoryAxis
        domain={{ y: [-4, 4] }}
        dependentAxis
        tickLabelComponent={<VictoryLabel style={{ fontSize: '5' }} />}
        tickValues={[-4, -3, -2, -1, 0, 1, 2, 3, 4]}
        style={{ grid: { stroke: 'lightgrey' } }}
      />
      <VictoryGroup
        offset={4}
        colorScale={colorScale ? colorScale : 'qualitative'}
      >
        <VictoryBar
          data={patientFormData}
          labelComponent={<VictoryLabel dx={({datum}) => datum.y < 0 ? -1: 1} style={{fontSize: '3'}}/>}
          labels={({datum}) => datum.y}/>
        <VictoryBar
          data={otherFormData}
          labelComponent={<VictoryLabel dx={({datum}) => datum.y < 0 ? -1: 1} style={{fontSize: '3'}}/>}
          labels={({datum}) => datum.y}/>
      </VictoryGroup>
      <VictoryAxis
        invertAxis
        offsetX={170}
        tickFormat={(tick) =>
          aivaFields.find((field) => field.id === tick).label
        }
        orientation="right"
        tickLabelComponent={<VictoryLabel style={{ fontSize: '5' }} />}
      />
    </VictoryChart>
  );
};

const ChartSingle = ({ fields, formData, colorScale }) => {
  return (
    <VictoryChart
      horizontal
      padding={{ right: 170, bottom: 50, left: 10 }}
      domain={{ y: [-4, 4] }}
      domainPadding={10}
      width={300}
      height={formData.length * 20}
    >
      <VictoryAxis
        domain={{ y: [-4, 4] }}
        dependentAxis
        tickLabelComponent={<VictoryLabel style={{ fontSize: '5' }} />}
        tickValues={[-4, -3, -2, -1, 0, 1, 2, 3, 4]}
        style={{ grid: { stroke: 'lightgrey' } }}
      />
      <VictoryBar
        style={{
          data: {fill: colorScale ?? PATIENT_CHART_COLOR}
        }}
        labelComponent={<VictoryLabel dx={({datum}) => datum.y < 0 ? -1: 1} style={{fontSize: '3'}}/>}
        labels={({datum}) => datum.y}
        data={formData}
      />
      <VictoryAxis
        invertAxis
        offsetX={170}
        orientation="right"
        tickFormat={(tick) => {
          const field = fields.find((field) => `${field.id}` === tick);
          return `${field?.useLabel ? field?.label?.fi : field?.name?.fi}`;
        }}
        tickLabelComponent={<VictoryLabel style={{ fontSize: '5' }} />}
      />
    </VictoryChart>
  );
};

const AivaDiagrams = (props) => {
  const [fetching, setFetching] = useState(true);
  const [patientBackgroundFormData, setPatientBackgroundFormData] = useState(
    []
  );
  const [patientFormData, setPatientFormData] = useState([]);
  const [relativeFormData, setRelativeFormData] = useState([]);
  const [proFormData, setProFormData] = useState([]);
  const [aivaFields, setAivaFields] = useState([]);
  const [backGroundFields, setBackgroundFields] = useState([]);
  const [otherInfo, setOtherInfo] = useState({});

  const dispatch = useDispatch();
  const history = useHistory();
  const { aivaProcessId } = props.match.params;
  const backToQuestionnaires = () => {
    history.push(`/questionnaires`);
  };

  useEffect(async () => {
    const { data } = await dispatch(fetchAivaProcess(aivaProcessId));
    const {
      patientBackgroundFormData,
      patientFormData,
      relativeFormData,
      proFormData,
    } = data;
    const patientBackgroundValues = await JSON.parse(
      patientBackgroundFormData.values
    );
    const patientValues = await JSON.parse(patientFormData.values);
    const relativeValues = relativeFormData
      ? await JSON.parse(relativeFormData.values)
      : {};
    const proValues = await JSON.parse(proFormData.values);
    const formConfig = await Dll.getAtomicForm(patientFormData.formId);
    const proFormConfig = await Dll.getAtomicForm(proFormData.formId);
    const backgroundFormConfig = await Dll.getAtomicForm(
      patientBackgroundFormData.formId
    );
    const aivaFields = [];
    formConfig.elements.forEach((element) => {
      if (element.meta?.aivaIdentifier && element.meta.aivaIdentifier !== '') {
        aivaFields.push({
          id: `${element.meta.aivaIdentifier}_${element.id}`,
          aivaIdentifier: element.meta.aivaIdentifier,
          fieldId: element.id,
          label: element.useLabel ? element.label?.fi : element.name?.fi,
        });
      }
    });
    setAivaFields(aivaFields);
    const otherInfoFields = [];
    proFormConfig.elements.forEach((element) => {
      if (
        element.meta?.aivaIdentifier &&
        ['yksilötekijät', 'ympäristötekijät', 'terveydentila'].includes(
          element.meta.aivaIdentifier
        )
      ) {
        otherInfoFields.push(element);
      }
    });
    const backgroundFields = [];
    backgroundFormConfig.elements.forEach((element) => {
      if (
        element.meta?.aivaIdentifier &&
        element.meta.aivaIdentifier !== '' &&
        element.type === 'range'
      ) {
        backgroundFields.push(element);
      }
    });
    setBackgroundFields(backgroundFields);
    const patientData = mapToChartData(patientValues, aivaFields);
    setPatientFormData(patientData);
    if (relativeFormData) {
      setRelativeFormData(mapToChartData(relativeValues, aivaFields));
    }
    setProFormData(mapToChartData(proValues, aivaFields));
    setOtherInfo(getOtherInfo(proValues, otherInfoFields));
    setPatientBackgroundFormData(
      mapToChartData(patientBackgroundValues, backgroundFields)
    );
    setFetching(false);
  }, []);

  const relativeKeys = relativeFormData.map((data) => data.x);
  const proKeys = proFormData.map((data) => data.x);
  const patientFormDataChart1Filtered =
    relativeKeys.length > 0
      ? patientFormData.filter((data) => relativeKeys.includes(data.x))
      : patientFormData.filter((data) => !proKeys.includes(data.x));
  const patientFormDataChart2Filtered = patientFormData.filter((data) =>
    proKeys.includes(data.x)
  );

  return (
    <div className="ot-mainview">
      <h2>
        Aivovammapoliklinikalla laadittu toimintakyvyn kuvaus ja suunnitelma
      </h2>

      <h3>Ympäristön vaikutus ja kouluaikainen oppiminen</h3>
      <p>
        <span style={{ color: PATIENT_CHART_COLOR }}>{'\u25FC'}</span> Potilaan
        vastaukset
      </p>
      {!fetching && (
        <ChartSingle
          fields={backGroundFields}
          formData={patientBackgroundFormData}
          colorScale={PATIENT_CHART_COLOR}
        />
      )}

      <h3>Yksilötekijät</h3>
      <p>
        {otherInfo['yksilötekijät'] && otherInfo['yksilötekijät'] !== ''
          ? otherInfo['yksilötekijät']
          : '-'}
      </p>

      <h3>Ympäristötekijät</h3>
      <p>
        {otherInfo['ympäristötekijät'] && otherInfo['ympäristötekijät'] !== ''
          ? otherInfo['ympäristötekijät']
          : '-'}
      </p>

      <h3>Suoritukset ja osallistuminen</h3>
      <p>
        <span style={{ color: PATIENT_CHART_COLOR }}>{'\u25FC'}</span> Potilaan
        vastaukset
        <br />
        <span style={{ color: RELATIVE_CHART_COLOR }}>{'\u25FC'}</span>{' '}
        Lähiomaisen vastaukset
      </p>
      {!fetching && (
        <Chart
          aivaFields={aivaFields}
          otherFormData={
            relativeFormData.length > 0
              ? relativeFormData
              : patientFormDataChart1Filtered.map((data) => {
                  return { x: data.x, y: 0 };
                })
          }
          patientFormData={patientFormDataChart1Filtered}
          colorScale={[PATIENT_CHART_COLOR, RELATIVE_CHART_COLOR]}
        />
      )}
      <h3>Ruumiin ja kehon toiminnot</h3>
      <p>
        <span style={{ color: PATIENT_CHART_COLOR }}>{'\u25FC'}</span> Potilaan
        vastaukset
        <br />
        <span style={{ color: PRO_CHART_COLOR }}>{'\u25FC'}</span> Ammattilaisen
        vastaukset
      </p>
      {!fetching && (
        <Chart
          aivaFields={aivaFields}
          otherFormData={proFormData}
          patientFormData={patientFormDataChart2Filtered}
          colorScale={[PATIENT_CHART_COLOR, PRO_CHART_COLOR]}
        />
      )}
      <h3>Terveydentila</h3>
      <p>
        {otherInfo['terveydentila'] && otherInfo['terveydentila'] !== ''
          ? otherInfo['terveydentila']
          : '-'}
      </p>
      <>
        <OTButton
          icon={'faAngleLeft'}
          variant={'outline'}
          message="back"
          style={{ marginBottom: '20px', marginTop: '20px' }}
          onClick={backToQuestionnaires}
        />
      </>
    </div>
  );
};

export default AivaDiagrams;
